import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type CommunityOut, type Role, type UserOut } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { ROLE_LABEL } from "../lib/role.helpers";
import { RoleBadge } from "../lib/role";

function canEditUser(me: UserOut, target: UserOut): boolean {
  if (me.role === "supadmin") return true;
  if (me.role === "admin") {
    return target.role === "admin" || target.role === "manager";
  }
  // Manager: only their own row.
  return target.id === me.id;
}

function canRemoveUser(me: UserOut, target: UserOut): boolean {
  if (target.id === me.id) return false;
  if (me.role === "supadmin") return true;
  if (me.role === "admin") {
    return target.role === "admin" || target.role === "manager";
  }
  return false;
}

function canToggleAccess(me: UserOut, target: UserOut): boolean {
  if (target.id === me.id) return false;
  return me.role === "supadmin";
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-primary/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-card shadow-lg">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="font-semibold text-foreground">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-muted-foreground text-sm"
          >
            ✕
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function CreateUserModal({
  me,
  onClose,
  onCreated,
}: {
  me: UserOut;
  onClose: () => void;
  onCreated: () => void;
}) {
  const assignableRoles: Role[] =
    me.role === "supadmin" ? ["supadmin", "admin", "manager"] : ["admin", "manager"];
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("manager");
  // SupaAdmin gets the implicit-default hint in the create-user modal:
  // when no explicit community is selected, the new admin auto-attaches
  // to the current default community. We surface that name so the
  // SupaAdmin understands what's about to happen.
  const communitiesQ = useQuery<CommunityOut[]>({
    queryKey: ["communities"],
    queryFn: () => api.listCommunities(),
    enabled: me.role === "supadmin",
  });
  const defaultCommunity = (communitiesQ.data ?? []).find((c) => c.is_default) ?? null;
  const m = useMutation({
    mutationFn: () =>
      api.createUser({
        first_name: firstName,
        last_name: lastName,
        email,
        role,
      }),
    onSuccess: () => onCreated(),
  });
  const err = m.error as { message?: string } | null;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !email.trim()) return;
    m.mutate();
  };
  return (
    <ModalShell title="Create User" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col text-xs text-muted-foreground">
            First name
            <input
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="border border-input rounded px-2 py-1.5 text-sm"
            />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">
            Last name
            <input
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="border border-input rounded px-2 py-1.5 text-sm"
            />
          </label>
        </div>
        <label className="flex flex-col text-xs text-muted-foreground">
          Email
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border border-input rounded px-2 py-1.5 text-sm"
          />
        </label>
        <label className="flex flex-col text-xs text-muted-foreground">
          Role
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="border border-input rounded px-2 py-1.5 text-sm"
          >
            {assignableRoles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </label>
        {me.role === "supadmin" && role === "admin" && defaultCommunity && (
          <div className="rounded border border-secondary/30 bg-secondary/10 px-2 py-1.5 text-xs text-secondary">
            Will be added to <span className="font-medium">{defaultCommunity.name}</span>{" "}
            (current default community).
          </div>
        )}
        {err && <div className="text-xs text-destructive">{err.message}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded border border-input text-foreground hover:bg-muted/40"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={m.isPending}
            className="text-sm px-3 py-1.5 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
          >
            {m.isPending ? "Creating…" : "Create user"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function EditUserModal({
  me,
  target,
  onClose,
  onSaved,
}: {
  me: UserOut;
  target: UserOut;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState(target.first_name ?? "");
  const [lastName, setLastName] = useState(target.last_name ?? "");
  const [role, setRole] = useState<Role>(target.role);
  const canEditRole = me.role === "supadmin" && target.id !== me.id;
  const m = useMutation({
    mutationFn: () => {
      const body: {
        first_name?: string;
        last_name?: string;
        role?: Role;
      } = {
        first_name: firstName,
        last_name: lastName,
      };
      if (canEditRole && role !== target.role) body.role = role;
      return api.updateUser(target.id, body);
    },
    onSuccess: () => onSaved(),
  });
  const err = m.error as { message?: string } | null;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    m.mutate();
  };
  return (
    <ModalShell title={`Edit ${target.email}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col text-xs text-muted-foreground">
            First name
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="border border-input rounded px-2 py-1.5 text-sm"
            />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">
            Last name
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="border border-input rounded px-2 py-1.5 text-sm"
            />
          </label>
        </div>
        <label className="flex flex-col text-xs text-muted-foreground">
          Role
          {canEditRole ? (
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="border border-input rounded px-2 py-1.5 text-sm"
            >
              {(["supadmin", "admin", "manager"] as Role[]).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          ) : (
            <div className="border border-border bg-muted/40 rounded px-2 py-1.5 text-sm text-foreground">
              {ROLE_LABEL[target.role]}
            </div>
          )}
        </label>
        <div className="flex flex-col text-xs text-muted-foreground">
          Communities / Teams
          {target.memberships && target.memberships.length > 0 ? (
            <div className="mt-1 flex flex-wrap gap-1">
              {target.memberships.map((mem, i) => (
                <span
                  key={i}
                  className="inline-flex rounded-full bg-muted text-foreground px-2 py-0.5 text-xs"
                >
                  {mem.team_name
                    ? `${mem.community_name} / ${mem.team_name}`
                    : mem.community_name}
                </span>
              ))}
            </div>
          ) : (
            <div className="mt-1 text-xs text-muted-foreground">No memberships.</div>
          )}
        </div>
        {err && <div className="text-xs text-destructive">{err.message}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded border border-input text-foreground hover:bg-muted/40"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={m.isPending}
            className="text-sm px-3 py-1.5 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
          >
            {m.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function RemoveUserConfirm({
  target,
  onClose,
  onRemoved,
}: {
  target: UserOut;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const m = useMutation({
    mutationFn: () => api.deleteUser(target.id),
    onSuccess: () => onRemoved(),
  });
  const err = m.error as { message?: string } | null;
  return (
    <ModalShell title="Remove user" onClose={onClose}>
      <p className="text-sm text-foreground">
        Remove <span className="font-semibold">{target.email}</span>? This cannot
        be undone.
      </p>
      {err && <div className="text-xs text-destructive mt-2">{err.message}</div>}
      <div className="flex justify-end gap-2 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="text-sm px-3 py-1.5 rounded border border-input text-foreground hover:bg-muted/40"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={m.isPending}
          onClick={() => m.mutate()}
          className="text-sm px-3 py-1.5 rounded bg-destructive text-white hover:bg-destructive/90 disabled:opacity-50"
        >
          {m.isPending ? "Removing…" : "Remove"}
        </button>
      </div>
    </ModalShell>
  );
}

function RevokeUserConfirm({
  target,
  onClose,
  onDone,
}: {
  target: UserOut;
  onClose: () => void;
  onDone: () => void;
}) {
  const isRevoking = target.is_active;
  const m = useMutation({
    mutationFn: () => api.updateUser(target.id, { is_active: !target.is_active }),
    onSuccess: () => onDone(),
  });
  const err = m.error as { message?: string } | null;
  return (
    <ModalShell title={isRevoking ? "Revoke access" : "Resume access"} onClose={onClose}>
      <p className="text-sm text-foreground">
        {isRevoking ? (
          <>
            Revoke login access for{" "}
            <span className="font-semibold">{target.email}</span>? They will be
            signed out immediately and cannot log in until access is resumed.
          </>
        ) : (
          <>
            Resume login access for{" "}
            <span className="font-semibold">{target.email}</span>?
          </>
        )}
      </p>
      {err && <div className="text-xs text-destructive mt-2">{err.message}</div>}
      <div className="flex justify-end gap-2 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="text-sm px-3 py-1.5 rounded border border-input text-foreground hover:bg-muted/40"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={m.isPending}
          onClick={() => m.mutate()}
          className={`text-sm px-3 py-1.5 rounded text-white disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] ${
            isRevoking
              ? "bg-destructive hover:bg-destructive/90"
              : "bg-primary hover:bg-primary/90"
          }`}
        >
          {m.isPending
            ? isRevoking
              ? "Revoking…"
              : "Resuming…"
            : isRevoking
              ? "Revoke access"
              : "Resume access"}
        </button>
      </div>
    </ModalShell>
  );
}

export function ManageUsersView({ me }: { me: UserOut }) {
  const qc = useQueryClient();
  const usersQ = useQuery<UserOut[]>({
    queryKey: ["users"],
    queryFn: () => api.users(),
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserOut | null>(null);
  const [removeTarget, setRemoveTarget] = useState<UserOut | null>(null);
  const [accessTarget, setAccessTarget] = useState<UserOut | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const list = usersQ.data ?? [];

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-border bg-card shadow-md animate-card-enter">
        <header className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <h2 className="font-semibold text-foreground">Manage Users</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{list.length} total</p>
          </div>
          {me.role !== "manager" && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="rounded bg-primary text-primary-foreground text-sm px-3 py-1.5 hover:bg-primary/90 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
            >
              Create User
            </button>
          )}
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/10 text-muted-foreground text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Last login</th>
                <th className="text-left px-3 py-2">Communities / Teams</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((u) => {
                const editable = canEditUser(me, u);
                const removable = canRemoveUser(me, u);
                const name =
                  u.full_name ||
                  [u.first_name, u.last_name].filter(Boolean).join(" ") ||
                  "—";
                const toggleable = canToggleAccess(me, u);
                return (
                  <tr key={u.id} className="border-t border-border align-top hover:bg-primary/5 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]">
                    <td className="px-3 py-2">{u.email}</td>
                    <td className="px-3 py-2">{name}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs ${
                          u.is_active
                            ? "bg-primary/15 text-primary"
                            : "bg-muted text-foreground"
                        }`}
                      >
                        {u.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <RoleBadge role={u.role} />
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {u.last_login_at ? fmtDateTime(u.last_login_at) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {u.memberships && u.memberships.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {u.memberships.map((m, i) => {
                            const label = m.team_name
                              ? `${m.community_name} / ${m.team_name}`
                              : m.community_name
                            return (
                              <span
                                key={`${u.id}-${i}`}
                                className="inline-flex rounded-full bg-muted text-foreground px-2 py-0.5 text-xs"
                                title={label}
                              >
                                {label}
                              </span>
                            )
                          })}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-2">
                        {editable && (
                          <button
                            type="button"
                            onClick={() => setEditTarget(u)}
                            className="text-xs text-foreground hover:underline"
                          >
                            Edit
                          </button>
                        )}
                        {toggleable && (
                          <button
                            type="button"
                            onClick={() => setAccessTarget(u)}
                            className={`text-xs hover:underline ${u.is_active ? "text-destructive" : "text-primary"}`}
                          >
                            {u.is_active ? "Revoke" : "Resume"}
                          </button>
                        )}
                        {removable && (
                          <button
                            type="button"
                            onClick={() => setRemoveTarget(u)}
                            className="text-xs text-destructive hover:underline"
                          >
                            Remove
                          </button>
                        )}
                        {!editable && !toggleable && !removable && (
                          <span className="text-xs text-muted-foreground">
                            {u.id === me.id ? "you" : ""}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {usersQ.isLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-6 text-center text-sm text-muted-foreground"
                  >
                    Loading…
                  </td>
                </tr>
              ) : (
                list.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-6 text-center text-sm text-muted-foreground"
                    >
                      No users yet.
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </section>

      {createOpen && (
        <CreateUserModal
          me={me}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            invalidate();
          }}
        />
      )}
      {editTarget && (
        <EditUserModal
          me={me}
          target={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            invalidate();
          }}
        />
      )}
      {removeTarget && (
        <RemoveUserConfirm
          target={removeTarget}
          onClose={() => setRemoveTarget(null)}
          onRemoved={() => {
            setRemoveTarget(null);
            invalidate();
          }}
        />
      )}
      {accessTarget && (
        <RevokeUserConfirm
          target={accessTarget}
          onClose={() => setAccessTarget(null)}
          onDone={() => {
            setAccessTarget(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}
